import { StrictMode } from 'react';
import { createRoot, type Root } from 'react-dom/client';

import widgetCss from './widget.css?inline';
import { Widget, type WidgetProps } from './Widget';

export type MountOptions = {
  /** Target element, or a CSS selector resolved against the document. */
  target: HTMLElement | string;
  props?: WidgetProps;
};

export type WidgetHandle = {
  /** Re-render the mounted widget with new props. */
  update: (props: WidgetProps) => void;
  /** Tear down the widget and release its React root. */
  unmount: () => void;
};

const STYLE_MARKER = 'data-wfw-style';
const CONTAINER_MARKER = 'data-wfw-container';

const roots = new WeakMap<HTMLElement, Root>();

const resolveTarget = (target: HTMLElement | string): HTMLElement => {
  if (typeof target === 'string') {
    const found = document.querySelector<HTMLElement>(target);
    if (!found) {
      throw new Error(`WebflowWidget: no element matches selector "${target}"`);
    }
    return found;
  }
  return target;
};

// Attach a shadow root to the host and render the widget inside it. The shadow
// boundary isolates the widget from the host page: Webflow's global (unlayered)
// stylesheet cannot reach past it, so the widget renders identically on a bare
// dev page and inside a full Webflow site. Only inheritable properties cross the
// boundary, and `.wfw-root` already pins font, color, line-height, and
// background, so those are neutralized too. The compiled CSS is imported as a
// string (`?inline`) and injected into the shadow root instead of the document
// head, keeping styles scoped to this instance.
const ensureShadowContainer = (host: HTMLElement): HTMLElement => {
  const shadow = host.shadowRoot ?? host.attachShadow({ mode: 'open' });

  if (!shadow.querySelector(`style[${STYLE_MARKER}]`)) {
    const style = document.createElement('style');
    style.setAttribute(STYLE_MARKER, '');
    style.textContent = widgetCss;
    shadow.appendChild(style);
  }

  let container = shadow.querySelector<HTMLElement>(`div[${CONTAINER_MARKER}]`);
  if (!container) {
    container = document.createElement('div');
    container.setAttribute(CONTAINER_MARKER, '');
    shadow.appendChild(container);
  }

  return container;
};

const render = (root: Root, props: WidgetProps): void => {
  root.render(
    <StrictMode>
      <Widget {...props} />
    </StrictMode>,
  );
};

export const unmount = (target: HTMLElement | string): void => {
  const host = resolveTarget(target);
  const root = roots.get(host);
  if (root) {
    root.unmount();
    roots.delete(host);
  }
};

export const mount = ({ target, props = {} }: MountOptions): WidgetHandle => {
  const host = resolveTarget(target);
  let root = roots.get(host);
  if (!root) {
    root = createRoot(ensureShadowContainer(host));
    roots.set(host, root);
  }
  render(root, props);

  return {
    update: nextProps => render(root, nextProps),
    unmount: () => unmount(host),
  };
};
